/* eslint-disable camelcase */
/**
 * @author Daniel Aguirre
 * @file saga.js
 * @description Dashboard Staff Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Services
import dashboardStaffService from '@nephro/services/dashboard-staff-service';

// Global actions
import {
  setResponse,
  setLoading,
  setLastVersiaUpdate,
  setClinics,
  setInsurers
} from '@nephro/redux/stores/app/actions';

// Constants
import {
  GET_RECOMMENDED_BY_SCHEDULING,
  GET_RECOMMENDED_BY_SCHEDULING_LIST,
  GET_ANTIBODY_ANTI_HBS,
  GET_ANTIBODY_ANTI_HBS_LIST,
  GET_INDI_BLOOD_PRESSURE_MONITORING,
  GET_INDI_GLUCOSE_MONITORING,
  GET_INDI_BLOOD_PRESSURE_MONITORING_LIST,
  GET_INDI_GLUCOSE_MONITORING_LIST,
  GET_ALBUMINURIA_PROGRESS,
  GET_ALBUMINURIA_PROGRESS_LIST,
  GET_ALBUMINURIA_PROGRESS_LIST_PLUS,
  GET_TFG_DETERIORATION,
  GET_TFG_DETERIORATION_LIST,
  GET_NUTRITIONAL_INDICATOR,
  GET_NUTRITIONAL_INDICATOR_LIST,
  GET_HEMOGLOBIN,
  GET_HEMOGLOBIN_LIST,
  GET_POTASSIUM,
  GET_POTASSIUM_LIST,
  GET_MISSED_APPOINTMENTS_BY_STAGE,
  GET_MISSED_APPOINTMENTS_BY_STAGE_LIST,
  GET_PROTEINURIA,
  GET_PROTEINURIA_LIST_PLUS,
  GET_PROTEINURIA_DETAIL,
  GET_ALBUMINURIA_F13,
  GET_ALBUMINURIA_F13_LIST,
  GET_ALBUMINURIA_F13_LIST_PLUS,
  GET_METABOLIC_CONTROL_DIABETES,
  GET_METABOLIC_CONTROL_DIABETES_LIST,
  GET_METABOLIC_CONTROL_DIABETES_LIST_PLUS,
  GET_PHOSPHORUS,
  GET_PHOSPHORUS_LIST,
  GET_ALBUMINA,
  GET_ALBUMINA_LIST,
  GET_PTH_INDICATOR,
  GET_PTH_INDICATOR_LIST,
  GET_SEROSTATUS_HEPATITIS_B,
  GET_SEROSTATUS_HEPATITIS_B_LIST,
  GET_RISK,
  GET_RISK_LIST,
  GET_RISK_LIST_PLUS,
  GET_BLOOD_PRESSURE,
  GET_BLOOD_PRESSURE_LIST,
  GET_VACCINE_SCHEDULING,
  GET_VACCINE_SCHEDULING_LIST,
  GET_POST_VACCINE_ANTI_HBS,
  GET_POST_VACCINE_ANTI_HBS_LIST,
  GET_RISK_REGISTER,
  GET_RISK_REGISTER_LIST,
  GET_LOSS_HEPATITIS_IMMUNITY,
  GET_LOSS_HEPATITIS_IMMUNITY_LIST,
  GET_CORRELATION_RISK_RATE_PROGRESSION,
  GET_CORRELATION_RISK_RATE_PROGRESSION_LIST,
  GET_CORRELATION_RISK_RATE_PROGRESSION_LIST_PLUS,
  GET_TREATMENT_OPTIONS,
  GET_TREATMENT_OPTIONS_LIST,
  GET_TREATMENT_OPTIONS_LIST_PLUS,
  GET_PSYCHOSOCIAL_RISK_PSYCHOLOGY,
  GET_PSYCHOSOCIAL_RISK_PSYCHOLOGY_LIST,
  GET_PSYCHOSOCIAL_RISK_PSYCHOLOGY_LIST_PLUS,
  GET_PSYCHOSOCIAL_RISK,
  GET_PSYCHOSOCIAL_RISK_LIST,
  GET_ACCESS,
  GET_ACCESS_LIST,
  GET_ACCESS_LIST_PLUS,
  GET_SCHEDULING_ALERT,
  GET_SCHEDULING_ALERT_LIST,
  GET_SCHEDULING_ALERT_LIST_PLUS,
  GET_PROTEIN_ENERGETIC_WEAR,
  GET_PROTEIN_ENERGETIC_WEAR_LIST,
  GET_PROTEIN_ENERGETIC_WEAR_LIST_PLUS,
  GET_GENERAL_SOCIAL_T_SOCIAL_RISK,
  GET_GENERAL_SOCIAL_T_SOCIAL_RISK_LIST,
  GET_GENERAL_SOCIAL_T_SOCIAL_RISK_LIST_PLUS,
  GET_ASSESMENT_IN_PSYCHOLOGY,
  GET_ASSESMENT_IN_PSYCHOLOGY_LIST,
  GET_NURSING_EDUCATION,
  GET_NURSING_EDUCATION_LIST,
  GET_NURSING_EDUCATION_LIST_PLUS,
  GET_DISSENT_WITHOUT_PSYCHOLOGY_ASSESSMENT,
  GET_DISSENT_WITHOUT_PSYCHOLOGY_ASSESSMENT_LIST,
  GET_DISSENT_WITHOUT_PSYCHOLOGY_ASSESSMENT_LIST_PLUS,
  GET_PATIENT_EVENTS,
  GET_PATIENT_EVENTS_LIST,
  GET_PATIENT_EVENTS_LIST_PLUS,
  GET_PERIODICITY_ATTENTION,
  GET_PERIODICITY_ATTENTION_LIST,
  GET_CONSTRUCTION_ACCESS_ASSESSMENT,
  GET_CONSTRUCTION_ACCESS_ASSESSMENT_LIST,
  GET_CONSTRUCTION_ACCESS_ASSESSMENT_LIST_PLUS,
  GET_FOLLOW_UP_ABSENTEEISM_ADHERENCE,
  GET_FOLLOW_UP_ABSENTEEISM_ADHERENCE_LIST,
  GET_GENERAL_SOCIAL_RISK_REGISTER,
  GET_GENERAL_SOCIAL_RISK_REGISTER_LIST,
  GET_GENERAL_SOCIAL_RISK_REGISTER_LIST_PLUS,
  GET_ADMON_CODES_BY_ENTITY,
  GET_MISSED_APPOINTMENTS_BY_STAGE_LIST_PLUS,
  GET_ANTIBODY_ANTI_HBS_LIST_PLUS,
  GET_VACCINE_SCHEDULING_LIST_PLUS,
  GET_LOSS_HEPATITIS_IMMUNITY_LIST_PLUS,
  GET_TFG_DETERIORATION_LIST_PLUS,
  GET_RISK_REGISTER_LIST_PLUS,
  GET_BLOOD_PRESSURE_LIST_PLUS,
  GET_NUTRITIONAL_INDICATOR_LIST_PLUS,
  GET_ALBUMINA_LIST_PLUS,
  GET_HEMOGLOBIN_LIST_PLUS,
  GET_PHOSPHORUS_LIST_PLUS,
  GET_POTASSIUM_LIST_PLUS,
  GET_FOLLOW_UP_ABSENTEEISM_ADHERENCE_LIST_PLUS,
  GET_PTH_INDICATOR_LIST_PLUS,
  GET_PSYCHOSOCIAL_RISK_LIST_PLUS,
  GET_INDI_BLOOD_PRESSURE_MONITORING_LIST_PLUS,
  GET_INDI_GLUCOSE_MONITORING_LIST_PLUS,
  GET_LAST_UPDATE,
  CLINICS_ENTITY_ID,
  INSURERS_ENTITY_ID
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get patient recommended scheduling from backend
 * @param { object } action - Action with payload data
 */
export function* getRecommendedByScheduling(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getRecommendedByScheduling,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient recommended scheduling list from backend
 * @param { object } action - Action with payload data
 */
export function* getRecommendedBySchedulingListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params = null } = data;

  try {
    const response = yield call(
      dashboardStaffService.getRecommendedBySchedulingListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient stage indicator from backend
 * @param { object } action - Action with payload data
 */
export function* getMissedAppointmentsByStage(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getMissedAppointmentsByStage,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient stage indicator list from backend
 * @param { object } action - Action with payload data
 */
export function* getMissedAppointmentsByStageList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, stage, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getMissedAppointmentsByStageList,
      clinicId,
      stage,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient stage indicator list from backend
 * @param { object } action - Action with payload data
 */
export function* getMissedAppointmentsByStageListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params = null } = data;

  try {
    const response = yield call(
      dashboardStaffService.getMissedAppointmentsByStageListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient antibody anti HBs from backend
 * @param { object } action - Action with payload data
 */
export function* getAntibodyAntiHBs(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAntibodyAntiHBs,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Indi Blood Pressure Monitoring from backend
 * @param { object } action - Action with payload data
 */
export function* getIndiBloodPressureMonitoring(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getIndiBloodPressureMonitoring,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Indi Blood Pressure Monitoring from backend
 * @param { object } action - Action with payload data
 */
export function* getIndiBloodPressureMonitoringList(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getIndiBloodPressureMonitoringList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Indi Blood Pressure Monitoring from backend
 * @param { object } action - Action with payload data
 */
export function* getIndiBloodPressureMonitoringListPlus(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getIndiBloodPressureMonitoringListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Indi Blood Pressure Monitoring from backend
 * @param { object } action - Action with payload data
 */
export function* getIndiGlucoseMonitoringList(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getIndiGlucoseMonitoringList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Indi Blood Pressure Monitoring from backend
 * @param { object } action - Action with payload data
 */
export function* getIndiGlucoseMonitoringListPlus(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getIndiGlucoseMonitoringListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Indi Blood Pressure Monitoring from backend
 * @param { object } action - Action with payload data
 */
export function* getIndiGlucoseMonitoring(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getIndiGlucoseMonitoring,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient antibody anti HBs list from backend
 * @param { object } action - Action with payload data
 */
export function* getAntibodyAntiHBsList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAntibodyAntiHBsList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient antibody anti HBs list plus from backend
 * @param { object } action - Action with payload data
 */
export function* getAntibodyAntiHBsListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAntibodyAntiHBsListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albuminuria progress from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminuriaProgress(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAlbuminuriaProgress,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient albuminuria progress list from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminuriaProgressList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, variacion, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAlbuminuriaProgressList,
      clinicId,
      variacion,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albuminuria progress list from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminuriaProgressListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getAlbuminuriaProgressListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albuminuria F13 list from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminuriaF13List(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, shift, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAlbuminuriaF13List,
      clinicId,
      shift,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albuminuria F13 list from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminuriaF13ListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAlbuminuriaF13ListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albuminuria F13 from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminuriaF13(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAlbuminuriaF13,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient TFG deterioration from backend
 * @param { object } action - Action with payload data
 */
export function* getTFGDeterioration(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getTFGDeterioration,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient TFG deterioration list from backend
 * @param { object } action - Action with payload data
 */
export function* getTFGDeteriorationList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, deterioroSeleccionado, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getTFGDeteriorationList,
      clinicId,
      deterioroSeleccionado,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient TFG deterioration list from backend
 * @param { object } action - Action with payload data
 */
export function* getTFGDeteriorationListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getTFGDeteriorationListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient nutritional indicator from backend
 * @param { object } action - Action with payload data
 */
export function* getNutritionalIndicator(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, stage } = data;

  try {
    const response = yield call(
      dashboardStaffService.getNutritionalIndicator,
      clinicId,
      stage
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient nutritional indicator list from backend
 * @param { object } action - Action with payload data
 */
export function* getNutritionalIndicatorList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, stage, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getNutritionalIndicatorList,
      clinicId,
      stage,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient nutritional indicator list from backend
 * @param { object } action - Action with payload data
 */
export function* getNutritionalIndicatorListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getNutritionalIndicatorListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient hemoglobin from backend
 * @param { object } action - Action with payload data
 */
export function* getHemoglobin(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(dashboardStaffService.getHemoglobin, clinicId);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient hemoglobin list from backend
 * @param { object } action - Action with payload data
 */
export function* getHemoglobinList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, clasificacionSeleccionada, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getHemoglobinList,
      clinicId,
      clasificacionSeleccionada,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient hemoglobin list from backend
 * @param { object } action - Action with payload data
 */
export function* getHemoglobinListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getHemoglobinListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient potassium from backend
 * @param { object } action - Action with payload data
 */
export function* getPotassium(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(dashboardStaffService.getPotassium, clinicId);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient potassium list from backend
 * @param { object } action - Action with payload data
 */
export function* getPotassiumList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPotassiumList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient potassium list from backend
 * @param { object } action - Action with payload data
 */
export function* getPotassiumListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPotassiumListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Metabolic Control Diabetes from backend
 * @param { object } action - Action with payload data
 */
export function* getMetabolicControlDiabetes(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getMetabolicControlDiabetes,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient Metabolic Control Diabetes list from backend
 * @param { object } action - Action with payload data
 */
export function* getMetabolicControlDiabetesList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getMetabolicControlDiabetesList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albuminuria progress list from backend
 * @param { object } action - Action with payload data
 */
export function* getMetabolicControlDiabetesListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getMetabolicControlDiabetesListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient phosphorus from backend
 * @param { object } action - Action with payload data
 */
export function* getPhosphorus(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(dashboardStaffService.getPhosphorus, clinicId);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient phosphorus list from backend
 * @param { object } action - Action with payload data
 */
export function* getPhosphorusList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPhosphorusList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient phosphorus list from backend
 * @param { object } action - Action with payload data
 */
export function* getPhosphorusListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPhosphorusListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albumina from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbumina(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(dashboardStaffService.getAlbumina, clinicId);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient albumina list from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminaList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAlbuminaList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient albumina list from backend
 * @param { object } action - Action with payload data
 */
export function* getAlbuminaListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAlbuminaListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient pth from backend
 * @param { object } action - Action with payload data
 */
export function* getPthIndicator(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, stage } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPthIndicator,
      clinicId,
      stage
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient pth list from backend
 * @param { object } action - Action with payload data
 */
export function* getPthIndicatorList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, stage, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPthIndicatorList,
      clinicId,
      classification,
      stage,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient pth list from backend
 * @param { object } action - Action with payload data
 */
export function* getPthIndicatorListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPthIndicatorListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient seroStatusHepatitisB from backend
 * @param { object } action - Action with payload data
 */
export function* getSeroStatusHepatitisB(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getSeroStatusHepatitisB,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient seroStatusHepatitisB list from backend
 * @param { object } action - Action with payload data
 */
export function* getSeroStatusHepatitisBList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getSeroStatusHepatitisBList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}
/**
 * Call service to get patient seroStatusHepatitisB list Plus from backend
 * @param { object } action - Action with payload data
 */
export function* getSeroStatusHepatitisBListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getSeroStatusHepatitisBListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient risk from backend
 * @param { object } action - Action with payload data
 */
export function* getRisk(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(dashboardStaffService.getRisk, clinicId);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient risk list from backend
 * @param { object } action - Action with payload data
 */
export function* getRiskList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, date, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getRiskList,
      clinicId,
      classification,
      date,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient risk list from backend
 * @param { object } action - Action with payload data
 */
export function* getRiskListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(dashboardStaffService.getRiskListPlus, params);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient proteinuria from backend
 * @param { object } action - Action with payload data
 */
export function* getProteinuriaDetail(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, proteinuriaDetail, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getProteinuriaIndicatorDetail,
      clinicId,
      proteinuriaDetail,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient proteinuria from backend
 * @param { object } action - Action with payload data
 */
export function* getProteinuria(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getProteinuriaIndicator,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient proteinuria from backend
 * @param { object } action - Action with payload data
 */
export function* getProteinuriaListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getProteinuriaIndicatorListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient blood pressure from backend
 * @param { object } action - Action with payload data
 */
export function* getBloodPressure(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getBloodPressureIndicator,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/** Call service to get patient risk from backend
 * @param { object } action - Action with payload data
 */
export function* getVaccineScheduling(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getVaccineScheduling,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient blood pressure list from backend
 * @param { object } action - Action with payload data
 */
export function* getBloodPressureList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getBloodPressureIndicatorList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient blood pressure list from backend
 * @param { object } action - Action with payload data
 */
export function* getBloodPressureListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getBloodPressureIndicatorListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/** Call service to get patient risk list from backend
 * @param { object } action - Action with payload data
 */
export function* getVaccineSchedulingList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, vaccineMonth, dose, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getVaccineSchedulingList,
      clinicId,
      vaccineMonth,
      dose,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/** Call service to get patient risk list from backend
 * @param { object } action - Action with payload data
 */
export function* getVaccineSchedulingListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getVaccineSchedulingListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient PostVaccineAntiHbs from backend
 * @param { object } action - Action with payload data
 */
export function* getPostVaccineAntiHbs(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPostVaccineAntiHbs,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient PostVaccineAntiHbs list from backend
 * @param { object } action - Action with payload data
 */
export function* getPostVaccineAntiHbsList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPostVaccineAntiHbsList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}
/**
 * Call service to get patient PostVaccineAntiHbs list from backend
 * @param { object } action - Action with payload data
 */
export function* getPostVaccineAntiHbsListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPostVaccineAntiHbsListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient RiskRegister from backend
 * @param { object } action - Action with payload data
 */
export function* getRiskRegister(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getRiskRegister,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient RiskRegister list from backend
 * @param { object } action - Action with payload data
 */
export function* getRiskRegisterList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getRiskRegisterList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient RiskRegister list from backend
 * @param { object } action - Action with payload data
 */
export function* getRiskRegisterListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getRiskRegisterListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient LossHepatitisImmunity from backend
 * @param { object } action - Action with payload data
 */
export function* getLossHepatitisImmunity(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getLossHepatitisImmunity,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient LossHepatitisImmunity list from backend
 * @param { object } action - Action with payload data
 */
export function* getLossHepatitisImmunityList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getLossHepatitisImmunityList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}
/**
 * Call service to get patient LossHepatitisImmunity list from backend
 * @param { object } action - Action with payload data
 */
export function* getLossHepatitisImmunityListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getLossHepatitisImmunityListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient CorrelationRiskRateProgression from backend
 * @param { object } action - Action with payload data
 */
export function* getCorrelationRiskRateProgression(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getCorrelationRiskRateProgression,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient CorrelationRiskRateProgression list from backend
 * @param { object } action - Action with payload data
 */
export function* getCorrelationRiskRateProgressionList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, proggression, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getCorrelationRiskRateProgressionList,
      clinicId,
      classification,
      proggression,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient proteinuria from backend
 * @param { object } action - Action with payload data
 */
export function* getCorrelationRiskRateProgressionListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getCorrelationRiskRateProgressionListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient TreatmentOption from backend
 * @param { object } action - Action with payload data
 */
export function* getTreatmentOptions(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getTreatmentOptions,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient TreatmentOption list from backend
 * @param { object } action - Action with payload data
 */
export function* getTreatmentOptionsList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getTreatmentOptionsList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get treatment from backend
 * @param { object } action - Action with payload data
 */
export function* getTreatmentOptionsListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getTreatmentOptionsListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}
/**
/**

/**
 * Call service to get Access from backend
 * @param { object } action - Action with payload data
 */
export function* getAccess(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(dashboardStaffService.getAccess, clinicId);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get access from backend
 * @param { object } action - Action with payload data
 */
export function* getAccessListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getAccessListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}
/**

/**
 * Call service to get Access list from backend
 * @param { object } action - Action with payload data
 */
export function* getAccessList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAccessList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Psychosocial Risk from backend
 * @param { object } action - Action with payload data
 */
export function* getPsychosocialRiskPsychology(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPsychosocialRiskPsychology,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Psychosocial Risk list from backend
 * @param { object } action - Action with payload data
 */
export function* getPsychosocialRiskPsychologyList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, month, riskParam, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPsychosocialRiskPsychologyList,
      clinicId,
      month,
      riskParam,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get constructionAccessAssessment from backend
 * @param { object } action - Action with payload data
 */
export function* getPsychosocialRiskPsychologyListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getPsychosocialRiskPsychologyListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Psychosocial Risk from backend
 * @param { object } action - Action with payload data
 */
export function* getPsychosocialRisk(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPsychosocialRisk,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Psychosocial Risk list from backend
 * @param { object } action - Action with payload data
 */
export function* getPsychosocialRiskList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPsychosocialRiskList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get access from backend
 * @param { object } action - Action with payload data
 */
export function* getPsychosocialRiskListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getPsychosocialRiskListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient SchedulingAlert from backend
 * @param { object } action - Action with payload data
 */
export function* getSchedulingAlert(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getSchedulingAlert,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient SchedulingAlert list from backend
 * @param { object } action - Action with payload data
 */
export function* getSchedulingAlertList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getSchedulingAlertList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient SchedulingAlert list from backend
 * @param { object } action - Action with payload data
 */
export function* getSchedulingAlertListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getSchedulingAlertListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient ProteinEnergeticWear from backend
 * @param { object } action - Action with payload data
 */
export function* getProteinEnergeticWear(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getProteinEnergeticWear,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient ProteinEnergeticWear list from backend
 * @param { object } action - Action with payload data
 */
export function* getProteinEnergeticWearList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getProteinEnergeticWearList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient SchedulingAlert list from backend
 * @param { object } action - Action with payload data
 */
export function* getProteinEnergeticWearListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getProteinEnergeticWearListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient GeneralSocialTSocialRisk from backend
 * @param { object } action - Action with payload data
 */
export function* getGeneralSocialTSocialRisk(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getGeneralSocialTSocialRisk,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient GeneralSocialTSocialRisk list from backend
 * @param { object } action - Action with payload data
 */
export function* getGeneralSocialTSocialRiskList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getGeneralSocialTSocialRiskList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get socialt plus from backend
 * @param { object } action - Action with payload data
 */
export function* getGeneralSocialTSocialRiskListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getGeneralSocialTSocialRiskListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Nursing Education from backend
 * @param { object } action - Action with payload data
 */
export function* getNursingEducation(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getNursingEducation,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Nursing Education list from backend
 * @param { object } action - Action with payload data
 */
export function* getNursingEducationList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getNursingEducationList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get nursing from backend
 * @param { object } action - Action with payload data
 */
export function* getNursingEducationListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getNursingEducationListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Assessment in Psychology from backend
 * @param { object } action - Action with payload data
 */
export function* getAssessmentInPsychology(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAssessmentInPsychology,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Assessment in Psychology list from backend
 * @param { object } action - Action with payload data
 */
export function* getAssessmentInPsychologyList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAssessmentInPsychologyList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Assessment in Psychology from backend
 * @param { object } action - Action with payload data
 */
export function* getDissentWithoutPsychologyAssessment(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getDissentWithoutPsychologyAssessmentF36,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Patient Events from backend
 * @param { object } action - Action with payload data
 */
export function* getPatientEvents(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPatientEvents,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Assessment in Psychology list from backend
 * @param { object } action - Action with payload data
 */
export function* getDissentWithoutPsychologyAssessmentList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getDissentWithoutPsychologyAssessmentF36List,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Assessment in Psychology list from backend
 * @param { object } action - Action with payload data
 */
export function* getDissentWithoutPsychologyAssessmentListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getDissentWithoutPsychologyAssessmentF36ListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Patient Events list from backend
 * @param { object } action - Action with payload data
 */
export function* getPatientEventsList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPatientEventsList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Patient Events list from backend
 * @param { object } action - Action with payload data
 */
export function* getPatientEventsListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getPatientEventsListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Periodicity Attention from backend
 * @param { object } action - Action with payload data
 */
export function* getPeriodicityAttention(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, frequency } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPeriodicityAttention,
      clinicId,
      frequency
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Periodicity Attention list from backend
 * @param { object } action - Action with payload data
 */
export function* getPeriodicityAttentionList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, frequency, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getPeriodicityAttentionList,
      clinicId,
      classification,
      frequency,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient ConstructionAccessAssessment from backend
 * @param { object } action - Action with payload data
 */
export function* getConstructionAccessAssessment(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getConstructionAccessAssessment,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient ConstructionAccessAssessment list from backend
 * @param { object } action - Action with payload data
 */
export function* getConstructionAccessAssessmentList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getConstructionAccessAssessmentList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get constructionAccessAssessment from backend
 * @param { object } action - Action with payload data
 */
export function* getConstructionAccessAssessmentListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getConstructionAccessAssessmentListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Follow Up Absenteeism Adherencefrom backend
 * @param { object } action - Action with payload data
 */
export function* getFollowUpAbsenteeismAdherence(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, phase } = data;

  try {
    const response = yield call(
      dashboardStaffService.getFollowUpAbsenteeismAdherence,
      clinicId,
      phase
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get Follow Up Absenteeism Adherence list from backend
 * @param { object } action - Action with payload data
 */
export function* getFollowUpAbsenteeismAdherenceList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, phase, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getFollowUpAbsenteeismAdherenceList,
      clinicId,
      classification,
      phase,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Follow Up Absenteeism Adherence list from backend
 * @param { object } action - Action with payload data
 */
export function* getFollowUpAbsenteeismAdherenceListPlus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;

  try {
    const response = yield call(
      dashboardStaffService.getFollowUpAbsenteeismAdherenceListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get General Social Risk Register from backend
 * @param { object } action - Action with payload data
 */
export function* getGeneralSocialRiskRegister(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getGeneralSocialRiskRegister,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get General Social Risk Register list from backend
 * @param { object } action - Action with payload data
 */
export function* getGeneralSocialRiskRegisterList(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, classification, limit, offset } = data;

  try {
    const response = yield call(
      dashboardStaffService.getGeneralSocialRiskRegisterList,
      clinicId,
      classification,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get social risk register from backend
 * @param { object } action - Action with payload data
 */
export function* getGeneralSocialRiskRegisterListPlus(action) {
  yield put(setResponse(false, false, false));
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { params } = data;
  try {
    const response = yield call(
      dashboardStaffService.getGeneralSocialRiskRegisterListPlus,
      params
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Admon Codes By Entity from backend
 * @param { object } action - Action with payload data
 */
export function* getAdmonCodesByEntity(action) {
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { entityId } = data;

  try {
    const response = yield call(
      dashboardStaffService.getAdmonCodesByEntity,
      entityId
    );

    if (entityId === CLINICS_ENTITY_ID) {
      yield put(setClinics(response.data));
    }

    if (entityId === INSURERS_ENTITY_ID) {
      yield put(setInsurers(response.data));
    }

    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get Admon Codes By Entity from backend
 * @param { object } action - Action with payload data
 */
export function* getLastUpdate(action) {
  yield put(setResponse(false, false, false));
  const { callback } = action;

  try {
    const response = yield call(dashboardStaffService.getLastUpdate);
    yield put(setLastVersiaUpdate(response.data.last_versia_update));
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Declare all component sagas
 */
export function* watchDashboardStaffSaga() {
  yield takeLatest(GET_RECOMMENDED_BY_SCHEDULING, getRecommendedByScheduling);
  yield takeLatest(
    GET_RECOMMENDED_BY_SCHEDULING_LIST,
    getRecommendedBySchedulingListPlus
  );
  yield takeLatest(GET_ANTIBODY_ANTI_HBS, getAntibodyAntiHBs);
  yield takeLatest(GET_ANTIBODY_ANTI_HBS_LIST, getAntibodyAntiHBsList);
  yield takeLatest(GET_ANTIBODY_ANTI_HBS_LIST_PLUS, getAntibodyAntiHBsListPlus);
  yield takeLatest(
    GET_INDI_BLOOD_PRESSURE_MONITORING,
    getIndiBloodPressureMonitoring
  );
  yield takeLatest(
    GET_INDI_BLOOD_PRESSURE_MONITORING_LIST,
    getIndiBloodPressureMonitoringList
  );
  yield takeLatest(
    GET_INDI_BLOOD_PRESSURE_MONITORING_LIST_PLUS,
    getIndiBloodPressureMonitoringListPlus
  );
  yield takeLatest(GET_INDI_GLUCOSE_MONITORING, getIndiGlucoseMonitoring);
  yield takeLatest(
    GET_INDI_GLUCOSE_MONITORING_LIST,
    getIndiGlucoseMonitoringList
  );
  yield takeLatest(
    GET_INDI_GLUCOSE_MONITORING_LIST_PLUS,
    getIndiGlucoseMonitoringListPlus
  );
  yield takeLatest(GET_ALBUMINURIA_PROGRESS, getAlbuminuriaProgress);
  yield takeLatest(GET_ALBUMINURIA_PROGRESS_LIST, getAlbuminuriaProgressList);
  yield takeLatest(
    GET_ALBUMINURIA_PROGRESS_LIST_PLUS,
    getAlbuminuriaProgressListPlus
  );
  yield takeLatest(GET_ALBUMINURIA_F13, getAlbuminuriaF13);
  yield takeLatest(GET_ALBUMINURIA_F13_LIST, getAlbuminuriaF13List);
  yield takeLatest(GET_ALBUMINURIA_F13_LIST_PLUS, getAlbuminuriaF13ListPlus);
  yield takeLatest(GET_TFG_DETERIORATION, getTFGDeterioration);
  yield takeLatest(GET_TFG_DETERIORATION_LIST, getTFGDeteriorationList);
  yield takeLatest(
    GET_TFG_DETERIORATION_LIST_PLUS,
    getTFGDeteriorationListPlus
  );
  yield takeLatest(GET_NUTRITIONAL_INDICATOR, getNutritionalIndicator);
  yield takeLatest(GET_NUTRITIONAL_INDICATOR_LIST, getNutritionalIndicatorList);
  yield takeLatest(
    GET_NUTRITIONAL_INDICATOR_LIST_PLUS,
    getNutritionalIndicatorListPlus
  );
  yield takeLatest(GET_HEMOGLOBIN, getHemoglobin);
  yield takeLatest(GET_HEMOGLOBIN_LIST, getHemoglobinList);
  yield takeLatest(GET_HEMOGLOBIN_LIST_PLUS, getHemoglobinListPlus);
  yield takeLatest(GET_POTASSIUM, getPotassium);
  yield takeLatest(GET_POTASSIUM_LIST, getPotassiumList);
  yield takeLatest(GET_POTASSIUM_LIST_PLUS, getPotassiumListPlus);
  yield takeLatest(GET_PHOSPHORUS, getPhosphorus);
  yield takeLatest(GET_PHOSPHORUS_LIST, getPhosphorusList);
  yield takeLatest(GET_PHOSPHORUS_LIST_PLUS, getPhosphorusListPlus);
  yield takeLatest(GET_ALBUMINA, getAlbumina);
  yield takeLatest(GET_ALBUMINA_LIST, getAlbuminaList);
  yield takeLatest(GET_ALBUMINA_LIST_PLUS, getAlbuminaListPlus);
  yield takeLatest(GET_RISK, getRisk);
  yield takeLatest(GET_RISK_LIST, getRiskList);
  yield takeLatest(GET_RISK_LIST_PLUS, getRiskListPlus);
  yield takeLatest(GET_SEROSTATUS_HEPATITIS_B, getSeroStatusHepatitisB);
  yield takeLatest(
    GET_SEROSTATUS_HEPATITIS_B_LIST,
    getSeroStatusHepatitisBListPlus
  );
  yield takeLatest(
    GET_MISSED_APPOINTMENTS_BY_STAGE,
    getMissedAppointmentsByStage
  );
  yield takeLatest(
    GET_MISSED_APPOINTMENTS_BY_STAGE_LIST,
    getMissedAppointmentsByStageList
  );
  yield takeLatest(
    GET_MISSED_APPOINTMENTS_BY_STAGE_LIST_PLUS,
    getMissedAppointmentsByStageListPlus
  );
  yield takeLatest(GET_PROTEINURIA, getProteinuria);
  yield takeLatest(GET_PROTEINURIA_LIST_PLUS, getProteinuriaListPlus);
  yield takeLatest(GET_PROTEINURIA_DETAIL, getProteinuriaDetail);
  yield takeLatest(GET_METABOLIC_CONTROL_DIABETES, getMetabolicControlDiabetes);
  yield takeLatest(
    GET_METABOLIC_CONTROL_DIABETES_LIST,
    getMetabolicControlDiabetesList
  );
  yield takeLatest(
    GET_METABOLIC_CONTROL_DIABETES_LIST_PLUS,
    getMetabolicControlDiabetesListPlus
  );
  yield takeLatest(GET_PTH_INDICATOR, getPthIndicator);
  yield takeLatest(GET_PTH_INDICATOR_LIST, getPthIndicatorList);
  yield takeLatest(GET_PTH_INDICATOR_LIST_PLUS, getPthIndicatorListPlus);
  yield takeLatest(GET_BLOOD_PRESSURE, getBloodPressure);
  yield takeLatest(GET_BLOOD_PRESSURE_LIST, getBloodPressureList);
  yield takeLatest(GET_BLOOD_PRESSURE_LIST_PLUS, getBloodPressureListPlus);
  yield takeLatest(GET_VACCINE_SCHEDULING, getVaccineScheduling);
  yield takeLatest(GET_VACCINE_SCHEDULING_LIST, getVaccineSchedulingList);
  yield takeLatest(
    GET_VACCINE_SCHEDULING_LIST_PLUS,
    getVaccineSchedulingListPlus
  );
  yield takeLatest(GET_POST_VACCINE_ANTI_HBS, getPostVaccineAntiHbs);
  yield takeLatest(
    GET_POST_VACCINE_ANTI_HBS_LIST,
    getPostVaccineAntiHbsListPlus
  );
  yield takeLatest(GET_RISK_REGISTER, getRiskRegister);
  yield takeLatest(GET_RISK_REGISTER_LIST, getRiskRegisterList);
  yield takeLatest(GET_RISK_REGISTER_LIST_PLUS, getRiskRegisterListPlus);
  yield takeLatest(GET_LOSS_HEPATITIS_IMMUNITY, getLossHepatitisImmunity);
  yield takeLatest(
    GET_LOSS_HEPATITIS_IMMUNITY_LIST,
    getLossHepatitisImmunityList
  );
  yield takeLatest(
    GET_LOSS_HEPATITIS_IMMUNITY_LIST_PLUS,
    getLossHepatitisImmunityListPlus
  );
  yield takeLatest(
    GET_CORRELATION_RISK_RATE_PROGRESSION,
    getCorrelationRiskRateProgression
  );
  yield takeLatest(
    GET_CORRELATION_RISK_RATE_PROGRESSION_LIST,
    getCorrelationRiskRateProgressionList
  );
  yield takeLatest(
    GET_CORRELATION_RISK_RATE_PROGRESSION_LIST_PLUS,
    getCorrelationRiskRateProgressionListPlus
  );
  yield takeLatest(GET_TREATMENT_OPTIONS, getTreatmentOptions);
  yield takeLatest(GET_TREATMENT_OPTIONS_LIST, getTreatmentOptionsList);
  yield takeLatest(
    GET_TREATMENT_OPTIONS_LIST_PLUS,
    getTreatmentOptionsListPlus
  );
  yield takeLatest(GET_ACCESS, getAccess);
  yield takeLatest(GET_ACCESS_LIST, getAccessList);
  yield takeLatest(GET_ACCESS_LIST_PLUS, getAccessListPlus);
  yield takeLatest(
    GET_PSYCHOSOCIAL_RISK_PSYCHOLOGY,
    getPsychosocialRiskPsychology
  );
  yield takeLatest(
    GET_PSYCHOSOCIAL_RISK_PSYCHOLOGY_LIST,
    getPsychosocialRiskPsychologyList
  );
  yield takeLatest(
    GET_PSYCHOSOCIAL_RISK_PSYCHOLOGY_LIST_PLUS,
    getPsychosocialRiskPsychologyListPlus
  );
  yield takeLatest(GET_PSYCHOSOCIAL_RISK, getPsychosocialRisk);
  yield takeLatest(GET_PSYCHOSOCIAL_RISK_LIST, getPsychosocialRiskList);
  yield takeLatest(
    GET_PSYCHOSOCIAL_RISK_LIST_PLUS,
    getPsychosocialRiskListPlus
  );
  yield takeLatest(GET_SCHEDULING_ALERT, getSchedulingAlert);
  yield takeLatest(GET_SCHEDULING_ALERT_LIST, getSchedulingAlertList);
  yield takeLatest(GET_SCHEDULING_ALERT_LIST_PLUS, getSchedulingAlertListPlus);
  yield takeLatest(GET_PROTEIN_ENERGETIC_WEAR, getProteinEnergeticWear);
  yield takeLatest(
    GET_PROTEIN_ENERGETIC_WEAR_LIST,
    getProteinEnergeticWearList
  );
  yield takeLatest(
    GET_PROTEIN_ENERGETIC_WEAR_LIST_PLUS,
    getProteinEnergeticWearListPlus
  );
  yield takeLatest(
    GET_GENERAL_SOCIAL_T_SOCIAL_RISK,
    getGeneralSocialTSocialRisk
  );
  yield takeLatest(
    GET_GENERAL_SOCIAL_T_SOCIAL_RISK_LIST,
    getGeneralSocialTSocialRiskList
  );
  yield takeLatest(
    GET_GENERAL_SOCIAL_T_SOCIAL_RISK_LIST_PLUS,
    getGeneralSocialTSocialRiskListPlus
  );
  yield takeLatest(GET_NURSING_EDUCATION, getNursingEducation);
  yield takeLatest(GET_NURSING_EDUCATION_LIST, getNursingEducationList);
  yield takeLatest(
    GET_NURSING_EDUCATION_LIST_PLUS,
    getNursingEducationListPlus
  );
  yield takeLatest(GET_ASSESMENT_IN_PSYCHOLOGY, getAssessmentInPsychology);
  yield takeLatest(
    GET_ASSESMENT_IN_PSYCHOLOGY_LIST,
    getAssessmentInPsychologyList
  );
  yield takeLatest(
    GET_DISSENT_WITHOUT_PSYCHOLOGY_ASSESSMENT,
    getDissentWithoutPsychologyAssessment
  );
  yield takeLatest(
    GET_DISSENT_WITHOUT_PSYCHOLOGY_ASSESSMENT_LIST,
    getDissentWithoutPsychologyAssessmentList
  );
  yield takeLatest(
    GET_DISSENT_WITHOUT_PSYCHOLOGY_ASSESSMENT_LIST_PLUS,
    getDissentWithoutPsychologyAssessmentListPlus
  );
  yield takeLatest(GET_PATIENT_EVENTS, getPatientEvents);
  yield takeLatest(GET_PATIENT_EVENTS_LIST, getPatientEventsList);
  yield takeLatest(GET_PATIENT_EVENTS_LIST_PLUS, getPatientEventsListPlus);
  yield takeLatest(GET_PERIODICITY_ATTENTION, getPeriodicityAttention);
  yield takeLatest(GET_PERIODICITY_ATTENTION_LIST, getPeriodicityAttentionList);
  yield takeLatest(
    GET_CONSTRUCTION_ACCESS_ASSESSMENT,
    getConstructionAccessAssessment
  );
  yield takeLatest(
    GET_CONSTRUCTION_ACCESS_ASSESSMENT_LIST,
    getConstructionAccessAssessmentList
  );
  yield takeLatest(
    GET_CONSTRUCTION_ACCESS_ASSESSMENT_LIST_PLUS,
    getConstructionAccessAssessmentListPlus
  );
  yield takeLatest(
    GET_FOLLOW_UP_ABSENTEEISM_ADHERENCE,
    getFollowUpAbsenteeismAdherence
  );
  yield takeLatest(
    GET_FOLLOW_UP_ABSENTEEISM_ADHERENCE_LIST,
    getFollowUpAbsenteeismAdherenceList
  );
  yield takeLatest(
    GET_FOLLOW_UP_ABSENTEEISM_ADHERENCE_LIST_PLUS,
    getFollowUpAbsenteeismAdherenceListPlus
  );
  yield takeLatest(
    GET_GENERAL_SOCIAL_RISK_REGISTER,
    getGeneralSocialRiskRegister
  );
  yield takeLatest(
    GET_GENERAL_SOCIAL_RISK_REGISTER_LIST,
    getGeneralSocialRiskRegisterList
  );
  yield takeLatest(
    GET_GENERAL_SOCIAL_RISK_REGISTER_LIST_PLUS,
    getGeneralSocialRiskRegisterListPlus
  );
  yield takeLatest(GET_ADMON_CODES_BY_ENTITY, getAdmonCodesByEntity);
  yield takeLatest(GET_LAST_UPDATE, getLastUpdate);
}
